import { useCallback, useState } from 'react';

import { useRemoveLocationImage } from '@/api/synced-locations';
import { useToast } from '@/components/ui/use-toast';
import { describeBulkImageRemoval, type BulkImageRemovalOutcome } from '@/lib/image-removal';

export interface BulkImageRemovalProgress {
  done: number;
  total: number;
}

interface BulkImageRemoval {
  /** Set from the start of a run until its last removal has settled. */
  progress: BulkImageRemovalProgress | undefined;
  removeImages: (imageUrls: string[]) => Promise<void>;
}

/**
 * Remove several partner images of one listing. The removal verb takes one
 * URL, so they are removed one request at a time, each awaiting the
 * mutation's refetch before the next (the server's optimistic lock would
 * refuse overlapping removals). A failure does not stop the rest: every
 * removal addresses its image by URL, so the others stay valid. One toast
 * reports the run when it ends.
 */
export function useBulkImageRemoval(syncedLocationId: string | undefined): BulkImageRemoval {
  const removeImageMutation = useRemoveLocationImage();
  const { toast } = useToast();
  const [progress, setProgress] = useState<BulkImageRemovalProgress | undefined>(undefined);
  // mutateAsync is stable across renders; depending on it, not the mutation result, keeps removeImages stable
  const { mutateAsync } = removeImageMutation;

  const removeImages = useCallback(async (imageUrls: string[]) => {
    const urls = [...new Set(imageUrls)];
    if (!syncedLocationId || urls.length === 0) return;
    setProgress({ done: 0, total: urls.length });
    const outcomes: BulkImageRemovalOutcome[] = [];
    try {
      for (const url of urls) {
        try {
          const result = await mutateAsync({ syncedLocationId, url });
          outcomes.push({ ok: true, result });
        } catch (error) {
          outcomes.push({ ok: false, error });
        }
        setProgress({ done: outcomes.length, total: urls.length });
      }
    } finally {
      setProgress(undefined);
    }
    const summary = describeBulkImageRemoval(outcomes);
    toast({
      variant: summary.failed ? 'destructive' : 'default',
      title: summary.title,
      description: summary.description,
    });
  }, [syncedLocationId, mutateAsync, toast]);

  return { progress, removeImages };
}
